import React, { useCallback, useEffect, useRef, useState } from "react";
import Moment from "react-moment";
import { Box, Grid, Tooltip, Typography } from "@mui/material";

interface Props {
  field: string;
  value: any;
  isDateField?: boolean;
  fullwidth?: boolean;
  truncateWithTooltip?: boolean;
}

const getDisplayText = (value: any, isDateField: boolean): string => {
  if (isDateField && value != null) {
    return String(value);
  }
  if (value == null || value === "") {
    return "";
  }
  return String(value);
};

const TruncatedValue = ({ text }: { text: string }) => {
  const textRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }
    setIsTruncated(element.scrollWidth > element.clientWidth);
  }, []);

  useEffect(() => {
    updateTruncation();
    const element = textRef.current;
    if (!element) {
      return undefined;
    }

    const observer = new ResizeObserver(updateTruncation);
    if (typeof observer.observe === "function") {
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateTruncation);
    return () => window.removeEventListener("resize", updateTruncation);
  }, [text, updateTruncation]);

  return (
    <Tooltip
      title={text}
      arrow
      disableHoverListener={!isTruncated}
      disableFocusListener={!isTruncated}
      disableTouchListener={!isTruncated}
    >
      <Typography ref={textRef} noWrap component="span" display="block">
        {text}
      </Typography>
    </Tooltip>
  );
};

export const CardItem = ({
  field,
  value,
  isDateField = false,
  fullwidth = false,
  truncateWithTooltip = false,
}: Props) => {
  const columnSpan = fullwidth ? 12 : 6;

  const renderValue = () => {
    if (isDateField && value != null) {
      return (
        <Typography>
          <Moment local={true} format="MM/DD/YYYY hh:mm A" date={value} />
        </Typography>
      );
    }

    const displayText = getDisplayText(value, isDateField);

    if (truncateWithTooltip && displayText) {
      return <TruncatedValue text={displayText} />;
    }

    return <Typography>{displayText}</Typography>;
  };

  return (
    <Grid container item direction="row" xs={columnSpan} md={columnSpan} sx={{ width: "100%" }}>
      <Grid item xs={fullwidth ? 3 : 5} md={fullwidth ? 2 : 5}>
        <Box color="text.secondary">
          <Typography>{`${field}: `}</Typography>
        </Box>
      </Grid>
      <Grid item xs={fullwidth ? 9 : 7} md={fullwidth ? 10 : 7} sx={{ minWidth: 0 }}>
        <Box
          color="text.primary"
          ml={fullwidth ? "30px" : "inherit"}
          sx={truncateWithTooltip ? { minWidth: 0, overflow: "hidden" } : undefined}
        >
          {renderValue()}
        </Box>
      </Grid>
    </Grid>
  );
};
